'use client';

import type { CreatorTaxBreakdown, V2GridBreakdown } from '@/lib/cache/rpc-cache';

/** Normalized pending-fees row used by header + Rewards + lobby stats. */
export interface PendingFeesCachedShape {
  fees: bigint;
  serverManagers: string[];
  v2GridIds: string[];
  v2Breakdown: V2GridBreakdown[];
  v1PendingEth: bigint;
  aggregateUsd: number | null;
  hasAnyClaimable: boolean;
  creatorTaxBreakdown: CreatorTaxBreakdown[];
}

export function emptyPendingFeesCached(): PendingFeesCachedShape {
  return {
    fees: 0n,
    serverManagers: [],
    v2GridIds: [],
    v2Breakdown: [],
    v1PendingEth: 0n,
    aggregateUsd: null,
    hasAnyClaimable: false,
    creatorTaxBreakdown: [],
  };
}

/**
 * Parse `/api/rpc/pending-fees/[address]` JSON into the cached query shape.
 */
export function parsePendingFeesResponse(json: unknown): PendingFeesCachedShape {
  const empty = emptyPendingFeesCached();
  if (!json || typeof json !== 'object') return empty;
  const data = (json as { data?: Record<string, unknown> }).data;
  if (!data || typeof data !== 'object') return empty;

  try {
    const breakdown = (data.v2GridBreakdown || []) as V2GridBreakdown[];
    const v2GridIds = breakdown
      .filter((g) => BigInt(g.rawPendingFees || g.pendingFees || '0') > 0n)
      .map((g) => g.gridId);
    return {
      fees: BigInt((data.pendingFees as string | undefined) ?? '0'),
      serverManagers: (data.managerAddresses || []) as string[],
      v2GridIds,
      v2Breakdown: breakdown,
      v1PendingEth: BigInt((data.v1PendingEth as string | undefined) ?? '0'),
      aggregateUsd: (data.aggregateUsd as number | null) ?? null,
      hasAnyClaimable: Boolean(data.hasAnyClaimable),
      creatorTaxBreakdown: (data.creatorTaxBreakdown || []) as CreatorTaxBreakdown[],
    };
  } catch {
    return empty;
  }
}

export async function fetchPendingFeesCached(address: string): Promise<PendingFeesCachedShape> {
  try {
    const res = await fetch(`/api/rpc/pending-fees/${address}`);
    if (!res.ok) return emptyPendingFeesCached();
    const json = await res.json();
    return parsePendingFeesResponse(json);
  } catch {
    return emptyPendingFeesCached();
  }
}
